'use client';
import { cn } from '@/lib/utils';

import { BlockComponentModel } from '@/cms/types/blok-component';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';
import { useTheme } from '@/theme/ThemeProvider';

import { Hero } from '@/decodo/blocks/Hero';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import LinkButton, {
  LinkButtonProps,
} from '@/decodo/components/atoms/LinkButton';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { VideoStrapiProps } from '@/decodo/components/atoms/Video/VideoStrapi';
import { FeatureListStrapiProps } from '@/decodo/features/FeatureListStrapi/FeatureListStrapiDC';
import { handleHeroButtonLinkClick } from '@/decodo/features/HomepageHero/helpers';

import { heroImageCloudflareLoader } from '@/utils/heroImageLoader';
import IconGoogleLetter from '~/svg/IconGoogleLetter.svg';

export type HeroStrapiProps = {
  title: string;
  description: RichTextFieldStrapiProps;
  image: ImageStrapiProps;
  links: LinkButtonStrapiProps[];
  addGoogleSignIn?: boolean;
  featureList?: FeatureListStrapiProps;
  extraButton?: 'googleSignIn';
  extraButtonLink?: string;
  extraButtonText?: string;
  video?: VideoStrapiProps;
  showMoneyBack?: boolean;
};

const extraButtons = {
  googleSignIn: (link: string, text: string) => (
    <LinkButton
      variant='outlined'
      color='secondary'
      href='#'
      onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
        handleHeroButtonLinkClick(e, link)
      }
      leftSection={<IconGoogleLetter />}
      classNames={{ root: 'w-full md:w-auto' }}
    >
      {text}
    </LinkButton>
  ),
};

export function HeroStrapi({
  block,
  ...rest
}: BlockComponentModel<HeroStrapiProps>) {
  const {
    title,
    description,
    image,
    links,
    extraButton,
    extraButtonLink,
    extraButtonText,
    video,
    showMoneyBack,
  } = block;

  function Links() {
    function modifyFirstLinkProps(index: number): Partial<LinkButtonProps> {
      if (index === 0) {
        return {
          variant: 'filled',
          color: 'primary',
        };
      }

      return {
        variant: 'outlined',
        color: 'secondary',
      };
    }

    if (!links?.length) return;

    return (
      <div className='flex w-full flex-wrap gap-6'>
        {links.map((link, index) => {
          const linkTarget = link.openInNewTab ? '_blank' : '_self';
          return (
            <LinkButtonStrapi
              key={link.url}
              block={link}
              inner={{
                classNames: { root: 'w-full md:w-auto' },
                ...modifyFirstLinkProps(index),
                onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
                  handleHeroButtonLinkClick(e, link.url, linkTarget),
              }}
            />
          );
        })}
        {extraButton &&
          extraButtonLink &&
          extraButtonText &&
          extraButtons[extraButton](extraButtonLink, extraButtonText)}
      </div>
    );
  }

  const { theme: globalTheme } = useTheme();
  const theme = rest?.theme || globalTheme;

  const fallbackThemeTemp = theme.includes('dark') ? 'dark' : 'light';

  return (
    <section
      className={cn(fallbackThemeTemp, 'bg-gray-25')}
      data-tracker-section={SECTION_NAMES.HERO}
    >
      <Hero
        image={
          <ImageStrapi
            block={{ ...image }}
            inner={{
              fetchPriority: 'high',
              loader: heroImageCloudflareLoader,
              classNames: { container: 'max-w-[586px] max-h-[586px]' },
            }}
            width={586}
            height={544}
          />
        }
        links={<Links />}
        title={title}
        description={
          description ? (
            <div data-tracker-component='description'>
              <RichTextFieldStrapi block={description} />
            </div>
          ) : undefined
        }
        videoProps={video ? { ...video } : undefined}
        showMoneyBack={showMoneyBack}
      />
    </section>
  );
}
